import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

const speedSegments = [
    { target: 19, duration: 1000, speed: 1 },    // Normal start
    { target: 25, duration: 500, speed: 1.5 },   // Speed up
    { target: 28, duration: 1200, speed: 0.3 },  // Slow down significantly
    { target: 53, duration: 1000, speed: 0.4 },  // Another slow segment
    { target: 81, duration: 1500, speed: 0.8 },  // Slightly slow
    { target: 100, duration: 800, speed: 1 }     // Final sprint
];

const timeouts = [];

export const Statement = ({ onAnswer }) => {
    const [progress, setProgress] = useState(0);
    const [showContent, setShowContent] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const [loadingPhase, setLoadingPhase] = useState('Initializing...');
    const [showCheckmark, setShowCheckmark] = useState(false);
    const animationRef = useRef(null);
    const startTimeRef = useRef(null);
    
    useEffect(() => {
        
        // Add initial fade in
        timeouts.push(setTimeout(() => setShowContent(true), 100));
        
        // Define speed variations (target percentage, duration in ms, speed multiplier)
        
        
        let currentSegment = 0;
        let lastProgress = 0;
        
        const animate = (timestamp) => {
            if (!startTimeRef.current) startTimeRef.current = timestamp;
            const elapsed = timestamp - startTimeRef.current;
            
            
            const segment = speedSegments[currentSegment];
            if (!segment) return;
            
            // Calculate progress for current segment
            const segmentProgress = Math.min(elapsed / segment.duration, 1);
            const easedProgress = easeInOutCubic(segmentProgress);
            
            const currentProgress = lastProgress +
                (segment.target - lastProgress) * easedProgress;
            
            setProgress(currentProgress);
            
            // Update loading phase message based on progress
            if (currentProgress < 25) {
                setLoadingPhase('Initializing analysis...');
            } else if (currentProgress < 45) {
                setLoadingPhase('Processing responses...');
            } else if (currentProgress < 65) {
                setLoadingPhase('Calculating preferences...');
            } else if (currentProgress < 85) {
                setLoadingPhase('Generating recommendations...');
            } else {
                setLoadingPhase('Finalizing results...');
            }
            
            // Move to next segment if current one is complete
            if (segmentProgress >= 1) {
                lastProgress = segment.target;
                currentSegment++;
                startTimeRef.current = timestamp;
            }
            
            animationRef.current = requestAnimationFrame(animate);
        };
        
        animationRef.current = requestAnimationFrame(animate);
        
        return () => {
            timeouts.forEach(clearTimeout);
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);
    
    useEffect(() => {
        if (progress >= 100) {
            // Show checkmark and wait before continuing
            setShowCheckmark(true);
            timeouts.push(setTimeout(() => {
                setIsExiting(true);
                setTimeout(() => onAnswer('continue'), 500);
            }, 1000)); // Give time for checkmark animation
        }
    }, [progress]);
    
    // Easing function for smooth animation
    const easeInOutCubic = (x) => {
        return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
    };
    
    const CircularLoader = () => (
        <div className="relative w-48 h-48">
            <svg
                className="w-full h-full -rotate-90 transform"
                viewBox="0 0 200 200"
            >
                <circle
                    className="text-gray-200 stroke-current"
                    strokeWidth="4"
                    cx="100"
                    cy="100"
                    r="85"
                    fill="none"
                />
                
                {/* Progress circle */}
                <circle
                    className="text-pink-500 stroke-current"
                    strokeWidth="6"
                    strokeLinecap="round"
                    cx="100"
                    cy="100"
                    r="85"
                    fill="none"
                    style={{
                        strokeDasharray: `${2 * Math.PI * 85}`,
                        strokeDashoffset: `${2 * Math.PI * 85 * (1 - progress / 100)}`,
                        transition: 'stroke-dashoffset 0.1s linear'
                    }}
                />
            </svg>
            
            {/* Center content */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                <span className="text-4xl font-bold text-gray-700">
                    {Math.round(progress)}%
                </span>
            </div>
        </div>
    );
    
    const CheckmarkAnimation = () => (
        <svg
            className="w-48 h-48"
            viewBox="0 0 375 375"
            preserveAspectRatio="xMidYMid meet"
        >
            <g transform="matrix(7.5,0,0,7.5,480,500)">
                <path
                    className="stroke-pink-500"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                    strokeWidth="8"
                    d="M-19,1.75 C-19,1.75 -7.5,13.25 -7.5,13.25 C-7.5,13.25 19,-13.25 19,-13.25"
                >
                    <animate
                        attributeName="stroke-dashoffset"
                        from="100"
                        to="0"
                        dur="0.6s"
                        fill="freeze"
                    />
                </path>
            </g>
        </svg>
    );
    
    return (
        <div className={classNames(
            "relative h-full flex flex-col items-center justify-center px-6",
            "transition-all duration-500 ease-in-out",
            isExiting ? "opacity-0" : "opacity-100"
        )}>
            <div
                className={classNames(
                    "flex flex-col items-center transition-all duration-500 ease-in-out",
                    showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                )}
            >
                {/* {!showCheckmark ? <CircularLoader /> : <CheckmarkAnimation />} */}
                <CircularLoader/>
                <h2 className="text-xl font-semibold text-gray-800 text-center mt-8">
                    {/* {showCheckmark ? "Analysis Complete" : "Analyzing Your Results"} */}
                    Analyzing Your Results
                </h2>
                <p className="mt-2 text-gray-500 text-center">
                    {loadingPhase}
                </p>
            </div>
        </div>
    );
};

export default Statement; 